/**
 * GZOO AI Marketing — Platform Admin API (tenant management).
 *
 * The SaaS owner (operator) surface for managing customer tenants: list, create, edit,
 * suspend/resume, enter (impersonate), and delete. Every endpoint is operator-gated on the
 * backend (api/v1/platform.py); this module just fronts it.
 */
import { apiClient } from './client';

/** The built-in subscription tiers (mirrors backend app/data/subscription_plans.py). */
export const PLAN_OPTIONS: { id: string; name: string; blurb: string }[] = [
  { id: 'free', name: 'Free', blurb: '5 campaigns · 1 user · 10GB' },
  { id: 'starter', name: 'Starter', blurb: '$29/mo · 10 campaigns · 5 users · 50GB' },
  { id: 'professional', name: 'Professional', blurb: '$79/mo · 25 campaigns · 15 users · 200GB' },
  { id: 'business', name: 'Business', blurb: '$199/mo · 50 campaigns · 50 users · 500GB' },
  { id: 'agency', name: 'Agency', blurb: '$499/mo · unlimited campaigns · 100 users · 1TB' },
  { id: 'enterprise', name: 'Enterprise', blurb: 'Custom · unlimited everything' },
];

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  industry: string | null;
  timezone: string | null;
  plan: string;
  plan_name: string;
  member_count: number;
  owner_email: string | null;
  owner_name: string | null;
  suspended: boolean;
  publishing_disabled: boolean;
  sandbox: boolean;
  is_agency: boolean;
  created_at: string | null;
  // The publishing profile linked to this tenant (operator-set). null = not linked yet, so the tenant
  // can't connect social accounts until the operator links one. Provider name never shown.
  zernio_profile_id: string | null;
}

export interface TenantDetail extends Tenant {
  brand_count: number;
  campaign_count: number;
  // Operator-set per-channel provider override (e.g. {twitter: "postforme"}). Missing/absent
  // channels resolve via whatever default the publish path applies ("auto"). Operator-only
  // write surface (PUT /platform/tenants/{org_id}/social-provider) — tenants never see this.
  social_provider_by_channel: Record<string, string>;
}

export interface ProfileOption {
  profile_id: string;
  name: string | null;
  account_count: number;
  platforms: string[];
  linked_to: string[];   // tenant names already linking this profile (avoid accidental double-link)
}

export interface CreateTenantInput {
  org_name: string;
  owner_email: string;
  owner_full_name: string;
  owner_password?: string;
  brand_name?: string;
  industry?: string;
  timezone?: string;
  plan?: string;
  sandbox?: boolean;
  // Operator-set per-channel provider mix to seed on the new tenant (required at the UI level —
  // the operator must make an explicit choice for every tenant before it can publish).
  provider_by_channel?: Record<string, string>;
}

export interface CreateTenantResult {
  tenant: Tenant;
  owner_email: string;
  temp_password: string;
}

export interface UpdateTenantInput {
  name?: string;
  industry?: string;
  timezone?: string;
  plan?: string;
  suspended?: boolean;
  sandbox?: boolean;
}

export interface ImpersonateResult {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
  tenant_name: string;
  acting_as_email: string;
}

export async function fetchTenants(): Promise<Tenant[]> {
  const { data } = await apiClient.get('/platform/tenants');
  return data;
}

export async function fetchTenant(id: string): Promise<TenantDetail> {
  const { data } = await apiClient.get(`/platform/tenants/${id}`);
  return data;
}

export async function createTenant(input: CreateTenantInput): Promise<CreateTenantResult> {
  const { data } = await apiClient.post('/platform/tenants', input);
  return data;
}

export async function updateTenant(id: string, patch: UpdateTenantInput): Promise<Tenant> {
  const { data } = await apiClient.patch(`/platform/tenants/${id}`, patch);
  return data;
}

export async function impersonateTenant(id: string): Promise<ImpersonateResult> {
  const { data } = await apiClient.post(`/platform/tenants/${id}/impersonate`);
  return data;
}

export async function deleteTenant(id: string): Promise<void> {
  await apiClient.delete(`/platform/tenants/${id}`);
}

/** Publishing profiles the operator can link to a tenant (derived from accounts already connected). */
export interface ProviderCapabilities {
  channels: string[];     // every channel the provider mix may reference
  zernio: string[];       // channels Zernio can publish
  postforme: string[];    // channels PostForMe can publish (no reddit — Zernio-only)
}

// Which channels each publishing provider can actually serve. The backend derives this from the same
// constant it validates against, so the operator pickers can never offer a pairing that would 400.
export async function fetchProviderCapabilities(): Promise<ProviderCapabilities> {
  const { data } = await apiClient.get('/platform/provider-capabilities');
  return data;
}

export async function fetchProfiles(): Promise<ProfileOption[]> {
  const { data } = await apiClient.get('/platform/zernio-profiles');
  return data;
}

/** Link (profile_id) or unlink (null) a publishing profile to a tenant. */
export async function setTenantProfile(id: string, profile_id: string | null): Promise<TenantDetail> {
  const { data } = await apiClient.put(`/platform/tenants/${id}/zernio-profile`, { profile_id });
  return data;
}

/** Set (merge over the existing mix) which provider publishes each of a tenant's channels.
 *  Operator-only — tenants never choose a provider. */
export async function setTenantProviderMix(
  orgId: string,
  byChannel: Record<string, string>,
): Promise<TenantDetail> {
  const { data } = await apiClient.put(`/platform/tenants/${orgId}/social-provider`, { by_channel: byChannel });
  return data;
}
